/**
 * Gerenciamento de Empresas/Filiais.
 * ADMIN_TENANT pode cadastrar múltiplas empresas para agrupar REPs por unidade.
 */
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { empresaApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { Empresa } from '../../types/api'

const UF_LIST = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO',
]

const FORM_EMPTY = {
  nome: '', cnpj: '', ie: '', endereco: '', cidade: '', uf: '',
}

function formatCnpj(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14)
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`
}

export function EmpresasPage() {
  const qc = useQueryClient()
  const { data: empresas, isLoading } = useQuery({ queryKey: ['empresas'], queryFn: empresaApi.list })

  const [showForm, setShowForm] = useState(false)
  const [editEmpresa, setEditEmpresa] = useState<Empresa | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [formError, setFormError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: () => {
      const cnpjRaw = form.cnpj.replace(/\D/g, '')
      const payload = {
        nome: form.nome,
        cnpj: cnpjRaw ? Number(cnpjRaw) : null,
        ie: form.ie || null,
        endereco: form.endereco || null,
        cidade: form.cidade || null,
        uf: form.uf || null,
      }
      return editEmpresa
        ? empresaApi.update(editEmpresa.id, payload)
        : empresaApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['empresas'] }); closeForm() },
    onError: (err) => setFormError(getErrorMessage(err)),
  })

  const toggleAtivoMutation = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) =>
      empresaApi.update(id, { ativo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresas'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => empresaApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresas'] }),
  })

  function openCreate() {
    setEditEmpresa(null); setForm(FORM_EMPTY); setFormError(null); setShowForm(true)
  }
  function openEdit(e: Empresa) {
    setEditEmpresa(e)
    setForm({
      nome: e.nome,
      cnpj: e.cnpj ? formatCnpj(String(e.cnpj).padStart(14, '0')) : '',
      ie: e.ie ?? '',
      endereco: e.endereco ?? '',
      cidade: e.cidade ?? '',
      uf: e.uf ?? '',
    })
    setFormError(null)
    setShowForm(true)
  }
  function closeForm() { setShowForm(false); setEditEmpresa(null) }

  const ativos = empresas?.filter(e => e.ativo) ?? []
  const inativos = empresas?.filter(e => !e.ativo) ?? []

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Empresas / Filiais</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Agrupe seus REPs por unidade ou filial
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nova Empresa</button>
      </div>

      {/* Modal de formulário */}
      {showForm && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeForm() }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>
              {editEmpresa ? 'Editar Empresa' : 'Nova Empresa'}
            </h2>
            {formError && (
              <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{formError}</div>
            )}
            <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveMutation.mutate() }} noValidate>
              <Field label="Razão Social / Nome">
                <input
                  className="input" required
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Empresa Exemplo Ltda."
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <Field label="CNPJ" optional>
                  <input
                    className="input"
                    value={form.cnpj}
                    onChange={e => setForm(f => ({ ...f, cnpj: formatCnpj(e.target.value) }))}
                    placeholder="00.000.000/0000-00"
                  />
                </Field>
                <Field label="Inscrição Estadual" optional>
                  <input
                    className="input"
                    value={form.ie}
                    onChange={e => setForm(f => ({ ...f, ie: e.target.value }))}
                    placeholder="000.000.000.000"
                  />
                </Field>
              </div>
              <Field label="Endereço" optional>
                <input
                  className="input"
                  value={form.endereco}
                  onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))}
                  placeholder="Rua, número, complemento"
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--space-4)' }}>
                <Field label="Cidade" optional>
                  <input
                    className="input"
                    value={form.cidade}
                    onChange={e => setForm(f => ({ ...f, cidade: e.target.value }))}
                    placeholder="São Paulo"
                  />
                </Field>
                <Field label="UF" optional>
                  <select
                    className="input"
                    style={{ width: 90 }}
                    value={form.uf}
                    onChange={e => setForm(f => ({ ...f, uf: e.target.value }))}
                  >
                    <option value="">—</option>
                    {UF_LIST.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? <PageLoading /> : !empresas?.length ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>🏢</div>
          <h3 style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>Nenhuma empresa cadastrada</h3>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Cadastre empresas ou filiais para organizar seus REPs.
          </p>
        </div>
      ) : (
        <>
          {ativos.length > 0 && (
            <EmpresaTable
              empresas={ativos}
              onEdit={openEdit}
              onToggle={(e) => toggleAtivoMutation.mutate({ id: e.id, ativo: false })}
              onDelete={(e) => { if (confirm(`Remover "${e.nome}"?`)) deleteMutation.mutate(e.id) }}
              toggleLabel="Desativar"
            />
          )}
          {inativos.length > 0 && (
            <>
              <h3 style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)', marginTop: 'var(--space-8)', marginBottom: 'var(--space-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Inativas
              </h3>
              <EmpresaTable
                empresas={inativos}
                onEdit={openEdit}
                onToggle={(e) => toggleAtivoMutation.mutate({ id: e.id, ativo: true })}
                onDelete={(e) => { if (confirm(`Remover "${e.nome}"?`)) deleteMutation.mutate(e.id) }}
                toggleLabel="Reativar"
                dimmed
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

function EmpresaTable({
  empresas, onEdit, onToggle, onDelete, toggleLabel, dimmed,
}: {
  empresas: Empresa[]
  onEdit: (e: Empresa) => void
  onToggle: (e: Empresa) => void
  onDelete: (e: Empresa) => void
  toggleLabel: string
  dimmed?: boolean
}) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', opacity: dimmed ? 0.7 : 1 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
            <th style={TH}>Razão Social</th>
            <th style={TH}>CNPJ</th>
            <th style={TH}>Cidade / UF</th>
            <th style={TH}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {empresas.map(e => (
            <tr key={e.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
              <td style={TD}>
                <div style={{ fontWeight: 500 }}>{e.nome}</div>
                {e.ie && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>IE: {e.ie}</div>}
              </td>
              <td style={TD}>
                {e.cnpj ? formatCnpj(String(e.cnpj).padStart(14, '0')) : <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
              </td>
              <td style={TD}>
                {e.cidade && e.uf ? `${e.cidade} / ${e.uf}` : e.cidade || e.uf || <span style={{ color: 'var(--color-gray-400)' }}>—</span>}
              </td>
              <td style={TD}>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => onEdit(e)}>Editar</button>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => onToggle(e)}>{toggleLabel}</button>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }} onClick={() => onDelete(e)}>Remover</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TH: React.CSSProperties = { padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: 600, fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }
const TD: React.CSSProperties = { padding: 'var(--space-3) var(--space-4)', verticalAlign: 'middle' }

function Field({ label, children, optional }: { label: string; children: React.ReactNode; optional?: boolean }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{optional && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}
